import { css } from '@emotion/react';
import {
  Button,
  TextButton,
  Badge,
  IconButton,
  IconSearch,
  IconUpload,
  IconPlus,
  IconChart,
  IconEllipsis,
} from '@flodesk/grain';
import { useNavigate } from 'react-router-dom';

// Figma MCP assets (valid 7 days)
const img1 = 'https://www.figma.com/api/mcp/asset/0910e2c9-c635-49a9-85ff-b8d2d0421323';
const img2 = 'https://www.figma.com/api/mcp/asset/69916d5e-eb54-4d35-887b-76e931973574';
const img3 = 'https://www.figma.com/api/mcp/asset/6c0fd861-d386-4e8e-8ed5-548255f97bda';
const img4 = 'https://www.figma.com/api/mcp/asset/81a72fdf-44ae-4019-90d1-a60f1737c43b';
const img5 = 'https://www.figma.com/api/mcp/asset/0e3eadb4-5f0a-4e02-978e-0186f7e16099';

type Product = {
  id: number;
  name: string;
  thumbnail: string;
  visibility: 'Public' | 'Hidden';
  price: string;
  stock: string;
  sold: string;
  totalSales: string;
  isHidden?: boolean;
};

const products: Product[] = [
  {
    id: 1,
    name: 'Custom Itinerary',
    thumbnail: img1,
    visibility: 'Public',
    price: '$15',
    stock: '∞',
    sold: '412',
    totalSales: '$6,518',
  },
  {
    id: 2,
    name: 'Custom itinerary plus',
    thumbnail: img2,
    visibility: 'Public',
    price: '$450',
    stock: '∞',
    sold: '22',
    totalSales: '$9,990',
  },
  {
    id: 3,
    name: 'the DIY Portugal Bundle',
    thumbnail: img3,
    visibility: 'Public',
    price: '$27',
    stock: '∞',
    sold: '1,055',
    totalSales: '$28,485',
  },
  {
    id: 4,
    name: '30 EXTRA MINUTES to chat about your trip',
    thumbnail: img4,
    visibility: 'Public',
    price: '$50',
    stock: '∞',
    sold: '8',
    totalSales: '$400',
  },
  {
    id: 5,
    name: 'the DIY Marrakech Bundle',
    thumbnail: img5,
    visibility: 'Hidden',
    price: '$24',
    stock: '∞',
    sold: '0',
    totalSales: '0',
    isHidden: true,
  },
];

const pageStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 72px 24px;
`;

const dashboardStyles = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 1140px;
  max-width: 100%;
`;

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const navStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const navItemActiveStyles = css`
  font-size: 24px;
  font-weight: 500;
  white-space: nowrap;
  cursor: default;
`;

const navItemStyles = css`
  font-size: 24px;
  font-weight: 500;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
`;

const headerButtonsStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const headerMenusStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const tableCardStyles = css`
  background-color: white;
  border-radius: 10px;
  padding: 32px 56px 12px;
  width: 1140px;
  max-width: 100%;
`;

const tableStyles = css`
  display: grid;
  grid-template-columns: 415px 120px 120px 100px 100px 100px 72px;
`;

const headerRowStyles = css`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
`;

const dataRowStyles = css`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  height: 100px;
  border-bottom: 1px solid #f3f2f1;

  &:last-child {
    border-bottom: none;
  }
`;

const colHeaderStyles = css`
  font-weight: 500;
  height: 36px;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const cellStyles = css`
  display: flex;
  align-items: center;
`;

const thumbnailStyles = css`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const productNameStyles = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const cellTextStyles = css`
  white-space: nowrap;
`;

const dimmedCellTextStyles = css`
  white-space: nowrap;
  opacity: 0.4;
`;

export default function ProductsPage() {
  const navigate = useNavigate();
  return (
    <div css={pageStyles}>
      <div css={dashboardStyles}>
        {/* Header */}
        <div css={headerStyles}>
          <nav css={navStyles}>
            <span css={navItemActiveStyles}>My products</span>
            <a href="/" css={navItemStyles}>
              Discounts
            </a>
          </nav>
          <div css={headerButtonsStyles}>
            <div css={headerMenusStyles}>
              <TextButton icon={<IconSearch />} iconPosition="left">
                Search
              </TextButton>
              <TextButton icon={<IconUpload />} iconPosition="left">
                Import
              </TextButton>
            </div>
            <Button
              variant="accent"
              icon={<IconPlus />}
              iconPosition="left"
              onClick={() =>
                navigate({
                  pathname: '/v1/products/new',
                })
              }
            >
              New product
            </Button>
          </div>
        </div>

        {/* Table card */}
        <div css={tableCardStyles}>
          <div css={tableStyles}>
            {/* Header row */}
            <div css={headerRowStyles}>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-right: 40px;
                `}
              >
                Product
              </div>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-left: 8px;
                `}
              >
                Visibility
              </div>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-left: 8px;
                `}
              >
                Price
              </div>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-left: 8px;
                `}
              >
                Stock
              </div>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-left: 8px;
                `}
              >
                Sold
              </div>
              <div
                css={css`
                  ${colHeaderStyles};
                  padding-left: 8px;
                `}
              >
                Total sales
              </div>
              <div />
            </div>

            {/* Data rows */}
            {products.map(p => (
              <div key={p.id} css={dataRowStyles}>
                <div
                  css={css`
                    ${cellStyles};
                    gap: 16px;
                    padding-right: 40px;
                  `}
                >
                  <img src={p.thumbnail} alt={p.name} css={thumbnailStyles} />
                  <p
                    css={css`
                      ${productNameStyles};
                      max-width: 224px;
                    `}
                  >
                    {p.name}
                  </p>
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    padding-left: 8px;
                  `}
                >
                  {p.visibility === 'Public' ? (
                    <Badge backgroundColor="#dcf2cf" color="#1f4002">
                      Public
                    </Badge>
                  ) : (
                    <Badge borderColor="rgba(55, 55, 52, 0.2)" color="#10100f">
                      Hidden
                    </Badge>
                  )}
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    padding-left: 8px;
                  `}
                >
                  <span css={p.isHidden ? dimmedCellTextStyles : cellTextStyles}>{p.price}</span>
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    padding-left: 8px;
                  `}
                >
                  <span css={p.isHidden ? dimmedCellTextStyles : cellTextStyles}>{p.stock}</span>
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    padding-left: 8px;
                  `}
                >
                  <span css={p.isHidden ? dimmedCellTextStyles : cellTextStyles}>{p.sold}</span>
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    padding-left: 8px;
                  `}
                >
                  <span css={p.isHidden ? dimmedCellTextStyles : cellTextStyles}>
                    {p.totalSales}
                  </span>
                </div>
                <div
                  css={css`
                    ${cellStyles};
                    justify-content: flex-end;
                  `}
                >
                  <IconButton icon={<IconChart />} variant="neutral" />
                  <IconButton icon={<IconEllipsis />} variant="neutral" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
